import * as XLSX from "xlsx";
import { parseDDMMYYYYToISO } from "src/helpers/dateHelper";
import type { POProductConfirmationInputField } from "src/types";

const RESTOCK_HEADER_PREFIX = "est restock date";

export interface UploadProductRowResult {
  productId: string;
  confirmedQty: string;
  estRestockDate: string;
  isDiscontinued: boolean;
  errors: string[];
}

export interface ParseConfirmationUploadResult {
  extraProductIds: string[];
  rowResults: UploadProductRowResult[];
  headerErrors: string[];
}

function getColumnIndexMap(headers: unknown[]): Record<string, number> | null {
  const map: Record<string, number> = {};
  const normalized = headers.map((h) =>
    String(h ?? "")
      .trim()
      .toLowerCase()
  );
  const idIdx = normalized.findIndex((h) => h === "id");
  const confirmedIdx = normalized.findIndex((h) => h === "confirmed qty");
  const restockIdx = normalized.findIndex((h) => h.includes(RESTOCK_HEADER_PREFIX));
  if (idIdx === -1 || confirmedIdx === -1 || restockIdx === -1) {
    return null;
  }
  map["id"] = idIdx;
  map["confirmedQty"] = confirmedIdx;
  map["estRestockDate"] = restockIdx;
  return map;
}

function getCellValue(row: unknown[], colIndex: number): string {
  const cell = row[colIndex];
  if (cell == null) return "";
  if (typeof cell === "number" && !Number.isNaN(cell)) return String(Math.floor(cell));
  return String(cell).trim();
}

function parseRestockDateCell(rawCell: unknown): string {
  if (rawCell == null || rawCell === "") return "";
  const str = String(rawCell).trim();
  if (!str) return "";
  if (str.toLowerCase() === "discontinued") return "";

  if (typeof rawCell === "number" && !Number.isNaN(rawCell)) {
    const serial = Math.floor(rawCell);
    if (serial >= 1 && serial <= 2958465) {
      const date = new Date((serial - 25569) * 86400 * 1000);
      if (!Number.isNaN(date.getTime())) {
        const y = date.getUTCFullYear();
        const m = date.getUTCMonth() + 1;
        const d = date.getUTCDate();
        return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      }
    }
    return str;
  }

  const iso = parseDDMMYYYYToISO(rawCell);
  return iso ?? str;
}

export function parseConfirmationUploadXlsx(
  file: File,
  validProductIds: Set<string>
): Promise<ParseConfirmationUploadResult> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        if (!data) {
          resolve({
            extraProductIds: [],
            rowResults: [],
            headerErrors: ["File is empty or could not be read."]
          });
          return;
        }
        const workbook = XLSX.read(data, { type: "binary" });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const json: unknown[][] = XLSX.utils.sheet_to_json(firstSheet, { header: 1, defval: "" });
        if (!Array.isArray(json) || json.length < 2) {
          resolve({
            extraProductIds: [],
            rowResults: [],
            headerErrors: ["File must have a header row and at least one data row."]
          });
          return;
        }
        const headerRow = json[0] as unknown[];
        const colMap = getColumnIndexMap(headerRow);
        if (!colMap) {
          resolve({
            extraProductIds: [],
            rowResults: [],
            headerErrors: [
              "File must contain columns: Id, Confirmed Qty, Est Restock Date (DD/MM/YYYY). Check the header row."
            ]
          });
          return;
        }
        const extraProductIds: string[] = [];
        const rowResults: UploadProductRowResult[] = [];
        for (let i = 1; i < json.length; i++) {
          const row = json[i] as unknown[];
          if (!Array.isArray(row)) continue;
          const productId = getCellValue(row, colMap["id"]);
          if (!productId) continue;
          const idTrimmed = productId.trim();
          if (!validProductIds.has(idTrimmed)) {
            if (!extraProductIds.includes(idTrimmed)) {
              extraProductIds.push(idTrimmed);
            }
            continue;
          }
          const confirmedQtyRaw = getCellValue(row, colMap["confirmedQty"]);
          const estRestockRawCell = row[colMap["estRestockDate"]];
          const estRestockRawStr = getCellValue(row, colMap["estRestockDate"]);

          let estRestockDate = "";
          let isDiscontinued = false;
          if (estRestockRawStr && estRestockRawStr.toLowerCase() === "discontinued") {
            isDiscontinued = true;
          } else if (estRestockRawCell != null && estRestockRawCell !== "") {
            estRestockDate = parseRestockDateCell(estRestockRawCell);
          }

          const confirmedQty = confirmedQtyRaw !== "" ? confirmedQtyRaw : "";

          rowResults.push({
            productId: idTrimmed,
            confirmedQty,
            estRestockDate,
            isDiscontinued,
            errors: []
          });
        }
        resolve({ extraProductIds, rowResults, headerErrors: [] });
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsBinaryString(file);
  });
}

export function applyConfirmationUploadResults(
  current: POProductConfirmationInputField[],
  rowResults: UploadProductRowResult[]
): POProductConfirmationInputField[] {
  const resultsByProductId = new Map(rowResults.map((r) => [r.productId.trim(), r]));
  return current.map((pc) => {
    const key = String(pc.productId).trim();
    const result = resultsByProductId.get(key);
    if (!result || result.errors.length > 0) return pc;
    return {
      ...pc,
      confirmedQty: result.confirmedQty !== "" ? result.confirmedQty : pc.confirmedQty,
      estRestockDate: result.estRestockDate,
      isDiscontinued: result.isDiscontinued
    };
  });
}
